// card-header.component.ts
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TagModule } from 'primeng/tag';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NotificationService } from 'app/services/notification.service';
import { AuditReportService } from 'app/services/Reports/audit-report-service';
import { FeatureFlagService } from 'app/services/feature-flag.service';
import { ShowFormHistoryComponent } from './show-form-history/show-form-history.component';
import { FormPresenceComponent } from 'app/components/client/workflow/forms/form-presence/form-presence.component';
import { FEATURE_FLAGS } from 'app/shared/constants/feature-flags.constants';

export interface StatusConfig {
	icon: string;
	severity: 'success' | 'info' | 'danger' | 'secondary';
	label: string;
}

@Component({
	selector: 'app-card-header',
	standalone: true,
	imports: [CommonModule, TagModule, FormPresenceComponent],
	template: `
		<div class="card-header">
			<ng-content select="[notification-top]"></ng-content>

			<div class="d-flex justify-content-between align-items-center">
				<div class="d-flex align-items-center">
					<span class="card-title-text mr-2">{{ title }}</span>
					<ng-content select="[before-status]"></ng-content>
					<div class="px-2 pb-1" *ngIf="currentStatus">
						<p-tag
							[icon]="currentStatus.icon"
							[severity]="currentStatus.severity"
							[value]="currentStatus.label"
							styleClass="mt-1"
						/>
					</div>
					<ng-content select="[after-status]"></ng-content>
					<button
						*ngIf="canRequestHistory && formTimelineEnabled"
						type="button"
						class="ai-timeline-fab ml-2"
						title="View Form Timeline"
						aria-label="View form updates timeline"
						(click)="openFormHistoryModal()"
					>
						✨
					</button>
				</div>
				<div class="d-flex align-items-center gap-2">
					<app-form-presence
						*ngIf="formId && formType"
						[formType]="formType"
						[formId]="formId"
						[mode]="presenceMode"
					></app-form-presence>
					<ng-content select="[header-actions]"></ng-content>
				</div>
			</div>

			<div class="card-subtitle mb-3" *ngIf="subtitle">{{ subtitle }}</div>

			<ng-content select="[notification-bottom]"></ng-content>
		</div>
	`,
	styleUrls: ['./card-header.component.css'],
	styles: [
		`
			.card-title-text {
				font-size: 1.5rem;
			}

			.card-subtitle {
				margin-top: 0.1rem;
				color: gray;
				font-size: 0.875rem;
				font-weight: 400;
			}
		`,
	],
})
export class CardHeaderComponent implements OnInit, OnChanges {
	@Input() title = '';
	@Input() subtitle = '';
	@Input() workflowStep = '';
	@Input() formId?: string | null;
	@Input() clientId?: string | null;
	@Input() overrideStatus?: StatusConfig | null;
	@Input() formType?: string;
	@Input() presenceMode: 'view' | 'edit' = 'view';

	currentStatus: StatusConfig;
	formChangeHistorySummary: string | null = null;
	formTimelineEnabled = false;

	// The cached summary belongs to one form. Without tracking which, navigating between two
	// forms of the same type reuses this component instance and shows the previous form's history.
	private cachedSummaryFormId: string | null = null;

	constructor(
		private readonly auditReportService: AuditReportService,
		private readonly notificationService: NotificationService,
		private readonly modalService: BsModalService,
		private readonly featureFlagService: FeatureFlagService
	) {}

	private statusMap: Record<string, StatusConfig> = {
		draft: {
			icon: 'pi pi-bookmark',
			severity: 'secondary',
			label: 'Draft',
		},
		completed: {
			icon: 'pi pi-check-circle',
			severity: 'success',
			label: 'Complete',
		},
		denied: {
			icon: 'pi pi-times',
			severity: 'danger',
			label: 'Denied',
		},
		pending: {
			icon: 'pi pi-info-circle',
			severity: 'info',
			label: 'Pending',
		},
	};

	ngOnInit(): void {
		if (this.clientId) {
			this.featureFlagService
				.isFeatureEnabledForClient(this.clientId, FEATURE_FLAGS.FORM_TIMELINE)
				.subscribe((enabled) => (this.formTimelineEnabled = enabled));
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['workflowStep'] || changes['overrideStatus']) {
			this.updateStatus();
		}

		if (changes['formId'] && this.formId !== this.cachedSummaryFormId) {
			this.formChangeHistorySummary = null;
			this.cachedSummaryFormId = null;
		}
	}

	get canRequestHistory(): boolean {
		return !!(this.formId && this.clientId);
	}

	updateStatus(): void {
		if (this.overrideStatus !== undefined && this.overrideStatus !== null) {
			this.currentStatus = this.overrideStatus;
			return;
		}

		if (!this.workflowStep) {
			this.currentStatus = null;
			return;
		}

		const normalizedStep = this.workflowStep.toLowerCase().trim();
		this.currentStatus = this.statusMap[normalizedStep] || this.statusMap['pending'];
	}

	openFormHistoryModal(): void {
		const modalRef = this.modalService.show<ShowFormHistoryComponent>(ShowFormHistoryComponent, {
			class: 'modal-dialog-centered modal-lg',
			animated: true,
			initialState: {
				summary: '',
				isLoading: true,
			},
		});

		if (!this.canRequestHistory) {
			this.updateModalState(modalRef, '', false);
			return;
		}

		if (this.formChangeHistorySummary !== null) {
			this.updateModalState(modalRef, this.formChangeHistorySummary, false);
			return;
		}

		this.loadFormHistory(modalRef);
	}

	private loadFormHistory(modalRef: BsModalRef<ShowFormHistoryComponent>): void {
		this.auditReportService.getChangeHistorySummary(this.clientId!, this.formId!).subscribe({
			next: (data) => {
				const summary = this.normalizeSummary(data);
				this.formChangeHistorySummary = summary;
				this.cachedSummaryFormId = this.formId ?? null;
				this.updateModalState(modalRef, summary, false);
			},
			error: (error) => {
				this.notificationService.showError(error, 'Error');
				this.updateModalState(modalRef, '', false);
			},
		});
	}

	private normalizeSummary(data: string | null | undefined): string {
		return (data ?? '').trim();
	}

	private updateModalState(modalRef: BsModalRef<ShowFormHistoryComponent>, summary: string, isLoading: boolean): void {
		const componentInstance = modalRef.content;
		if (componentInstance) {
			componentInstance.summary = summary;
			componentInstance.isLoading = isLoading;
		}
	}
}
